import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import Button from '../../../composants/Button/Button';
import Banner from '../../../composants/Banner/Banner';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import MailPreview from '../../../composants/MailPreview/MailPreview';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { useAuth } from '../../../contexts/AuthContext';
import { getStatusColor } from '../../../utils/entityUtils';
// Sections mutualisees avec la case soeur ACHETEUR (OperationMarche) : purs composants d'affichage
// (aucun fetch propre, aucune action acheteur), alimentes par (operation, retenu).
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationMarchePreparation from '../../SuiviOperationMSV2/components/OperationMarchePreparation';
import { useMarcheDocuments } from '../../SuiviOperationMSV2/hooks/useMarcheDocuments';
import { formatDateHeure } from '../../SuiviOperationMSV2/domain/analyse/dates';
import { getNotificationDate } from '../../SuiviOperationMSV2/domain/analyse/historique';
import { buildMailPreview } from '../../SuiviOperationMSV2/domain/marche/mailTemplate';
import { OperationMS } from '../../SuiviOperationMSV2/types';
import { CommandeAnalyse } from '../../SuiviOperationMSV2/domain/analyse/types';
import { CommandeMS, TabKind } from '../types';
import { computeMarcheState, designatedSignataireLabel, positionCandidat } from '../domain/commandeMsMarche';
import { notificationMarche } from '../domain/commandeMsCandidature';
import { ctaVersMessagerie } from '../domain/commandeMsNextTab';
import CommandeMsMarcheCandidat from './CommandeMsMarcheCandidat';
import {
  signMarchePrivate,
  downloadSignedMarche,
  fetchSignedMarcheBlob,
} from '../services/commandeMsService';
// Les classes opms__* (accordeons, recap, preview PDF, lignes candidats, barre d'actions) vivent dans
// la CSS de la page acheteur ; les composants reutilises ne l'importent pas eux-memes -> on la charge.
import '../../SuiviOperationMSV2/SuiviOperationMSV2.css';
import './CommandeMsMarche.css';

interface CommandeMsMarcheProps {
  commande: CommandeMS;
  // Rafraichit la commande apres la signature (remplace le window.location.reload du legacy).
  refetch: () => Promise<void> | void;
  // Bascule d'onglet par `kind` : "Aller dans la messagerie" apres signature.
  goToTab?: (kind: TabKind) => void;
}

// Onglet "Notifications et signatures" de la vue PRESTATAIRE commande-MS (V2).
//
// PARITE avec la case soeur ACHETEUR (OperationMarche) : memes sections, dans le meme ordre —
// Preparation du Marche Subsequent / Signature et notification / Rappel des documents / Information
// aux candidats. Trois divergences ASSUMEES, dictees par le role :
//   1. Aucune action acheteur (Signer et notifier, demande de signature a un signataire, Annuler la
//      consultation) ni le bandeau "standstill" (qui parle d'informer les candidats evinces).
//      Actions presta : signer son marche, telecharger le marche signe, imprimer, aller en messagerie.
//   2. « Information aux candidats » n'affiche QUE la ligne de ce prestataire (les autres candidats ne
//      le regardent pas) et sans bouton d'envoi : il constate sa position et sa date de notification.
//   3. La section « Coordonnees » du fork presta est supprimee (l'acheteur ne l'a pas).
//
// Signature presta : POST /subsequents/private/sign/ (mode automatique, fidele a l'oracle
// handleSignPrivate l.483-520 ; le bouton oracle ignorait signatureMode -> on reproduit l'auto-sign).
// Gating 100% id-based via le domaine ; id en argument (zero window.location) ; refetch (zero reload) ;
// erreurs en ValidationPopUp (zero alert).
const CommandeMsMarche: React.FC<CommandeMsMarcheProps> = ({ commande, refetch, goToTab }) => {
  const { user } = useAuth();

  const [signing, setSigning] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const operationId = commande.operation?.id;
  const commandeId = commande.id;
  const numero = commande.operation?.numero;

  // La commande presta joue le role du « retenu » des sections mutualisees : elle porte le contrat
  // (id / numero / lot / entite), seules donnees que buildPreparationRecap et useMarcheDocuments lisent.
  const operation = (commande.operation ?? {}) as unknown as OperationMS;
  const retenu = commande as unknown as CommandeAnalyse;

  const state = computeMarcheState(commande, user?.id);
  const position = positionCandidat(commande);
  const notification = notificationMarche(commande);
  // CTA de bascule vers la Messagerie. `canSign` (statut 8|10) et l'ouverture de la messagerie
  // (statut 11) s'excluent : le CTA n'apparait donc jamais a cote du bouton orange "Signer le
  // marche" — l'onglet ne propose qu'une seule action orange a la fois.
  const ctaMessagerie = ctaVersMessagerie(commande);

  // Documents associes au marche (Contrat + CCTP), memes endpoints que cote acheteur.
  const { documents: marcheDocuments, loading: marcheDocsLoading } = useMarcheDocuments(retenu);

  // Previsualisation du PDF du marche (meme document que le telechargement).
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  useEffect(() => {
    if (!numero) return undefined;
    let revoked = false;
    let url: string | null = null;
    setLoadingPdf(true);
    fetchSignedMarcheBlob(numero)
      .then((blob) => {
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      })
      .catch(() => setPdfBlobUrl(null))
      .finally(() => setLoadingPdf(false));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [numero]);

  // Date a laquelle CE prestataire a ete informe de sa position. Retenu : notifie automatiquement a la
  // signature de l'acheteur (bloc `notification`, present au marche signe). Non-retenu : evenement
  // « Notification » de l'historique. Source unique : la ligne candidat ET l'apercu du mail.
  const dateNotification =
    position === 'retenu'
      ? notification?.dateHeure ?? null
      : formatDateHeure(getNotificationDate(commande.historique, commandeId) ?? '');

  const handleSign = async () => {
    if (!operationId) return;
    setSigning(true);
    try {
      await signMarchePrivate(operationId, commandeId);
      await refetch();
    } catch (err) {
      setErrorModal(err instanceof Error ? err.message : 'Erreur lors de la signature du marché');
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async () => {
    if (!numero) return;
    try {
      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      await downloadSignedMarche(numero, `${annee}${numero} (signed).pdf`);
    } catch (err) {
      setErrorModal(
        err instanceof Error ? err.message : 'Erreur lors du téléchargement du marché signé',
      );
    }
  };

  const generateMail = useCallback(
    (cmd: CommandeAnalyse): string => buildMailPreview(cmd, operation, dateNotification),
    [operation, dateNotification],
  );

  // Bandeau de notification du marche, rendu EN HAUT et EN BAS de l'onglet : il annonce le numero
  // definitif, qui a notifie et quand, et l'ouverture des onglets Messagerie / Evaluation.
  const bandeauNotification = notification ? (
    <Banner
      variant="success"
      message={`Le marché subséquent N°${notification.numero} vous a été notifié le ${notification.dateHeure} par ${notification.nom}. Les onglets de messagerie opérationnelle et d'évaluation de l'acheteur sont désormais actifs.`}
    />
  ) : null;

  const printRef = useRef<HTMLDivElement>(null);
  const lotTechnique = commande.operation?.lot_technique;
  const printSubtitle = lotTechnique
    ? `${lotTechnique.id} - ${lotTechnique.nom}, ${commande.operation?.id_lot_departement ?? ''} - ${
        commande.operation?.lot_departement?.departement?.nom ?? ''
      }`
    : 'Informations de lot non disponibles';

  // Bouton de telechargement defini UNE fois : rendu en bas de la section « Signature et
  // notification » ET dans la barre d'actions du bas, comme la case acheteur.
  const boutonTelecharger = (
    <Button
      value="Télécharger le marché subséquent signé"
      variant="secondary"
      icon={<PdfIcon />}
      onClick={handleDownload}
    />
  );

  return (
    <div className="opms__marche" ref={printRef}>
      {bandeauNotification}

      <OperationMarchePreparation operation={operation} retenu={retenu} />

      <SectionPliable title="Signature et notification" defaultOpen>
        <div className="opms__marche-body">
          {/* Previsualisation du marche — disponible des que le PDF existe. */}
          <div className="opms__marche-preview">
            <div className="opms__marche-pdf opms__marche-pdf--narrow">
              {loadingPdf ? (
                <p className="opms__marche-pdf-empty">Chargement du document PDF...</p>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title="Prévisualisation du Marché Subséquent"
                  className="opms__marche-iframe"
                />
              ) : (
                <div className="opms__marche-pdf-empty">
                  <p>Le document PDF du marché subséquent n'est pas encore disponible.</p>
                  <p>Il sera généré lors de la validation de l'opération.</p>
                </div>
              )}
            </div>
          </div>

          {state.canSign && (
            <div className="signature-pending">
              <h4>Le marché est prêt pour votre signature</h4>
              <p>
                L'acheteur a validé et signé le marché. C'est maintenant à vous de signer pour
                finaliser le marché subséquent.
              </p>
              <Button
                type="button"
                onClick={handleSign}
                value={signing ? 'Signature en cours...' : 'Signer le marché'}
                disabled={signing}
              />
            </div>
          )}

          {state.showNotDesignated && (
            <div className="signature-pending">
              <h4>Vous n'êtes pas le signataire désigné pour ce marché</h4>
              <p>
                Seul le signataire défini{' '}
                <span className="signataire">({designatedSignataireLabel(commande)})</span> peut
                signer électroniquement ce document.
              </p>
            </div>
          )}

          {state.isWaiting && (
            <div className="signature-pending">
              <h4>Signature du marché en cours</h4>
              <p>
                Le marché est en cours de finalisation. Vous serez notifié dès qu'une action de
                votre part sera requise.
              </p>
            </div>
          )}

          {state.isExpired && (
            <div className="signature-pending">
              <h4>Marché expiré</h4>
              <p>Le délai est dépassé : ce marché subséquent ne peut plus être signé.</p>
            </div>
          )}

          {state.isCancelled && (
            <div className="signature-pending">
              <h4>Marché annulé</h4>
              <p>Ce marché subséquent a été annulé et ne peut plus être signé.</p>
            </div>
          )}

          {/* Telechargement du marche signe, EN BAS de la section — apres la previsualisation, dans
              l'ordre de lecture. Il manquait ici : il n'existait que dans la barre d'actions du bas,
              alors que la case acheteur le propose aux deux endroits. Meme JSX que la barre. */}
          {state.isSigned && (
            <div className="opms__marche-sign-bottom no-print">{boutonTelecharger}</div>
          )}
        </div>
      </SectionPliable>

      <SectionPliable title="Rappel des documents associés à ce marché" defaultOpen>
        <div className="opms__marche-docs">
          <div className="opms__marche-docs-row">
            {marcheDocsLoading && marcheDocuments.length === 0 ? (
              <p className="opms__marche-pdf-empty">Chargement des documents...</p>
            ) : (
              <DocumentsList documents={marcheDocuments} variant="grid" />
            )}
          </div>
        </div>
      </SectionPliable>

      {/* Une seule ligne : celle de ce prestataire. Rendue une fois que l'acheteur a tranche. */}
      {position && (
        <CommandeMsMarcheCandidat
          commande={commande}
          position={position}
          dateNotification={dateNotification}
          onSeeMessage={() => setIsPreviewOpen(true)}
        />
      )}

      {/* Rappel du bandeau juste avant les boutons (no-print : deja en tete de page). */}
      <div className="no-print">{bandeauNotification}</div>

      <div className="opms__actions no-print">
        <PrintButton
          targetRef={printRef}
          value="Imprimer la page"
          fileName={`${numero ?? ''} - Notifications et signatures`}
          suiviHeaderProps={{
            titre: commande.operation?.nom ?? 'Marché subséquent',
            sousTitre: '',
            subtitle: printSubtitle,
            status: commande.status?.nom || 'Statut inconnu',
            statusColor: getStatusColor(commande.status?.nom || ''),
            delais: commande.expire_at ?? commande.operation?.expire_at ?? undefined,
          }}
        />

        {state.canSign && (
          <Button
            value={signing ? 'Signature en cours...' : 'Signer le marché'}
            variant="primary"
            onClick={handleSign}
            disabled={signing}
          />
        )}

        {state.isSigned && boutonTelecharger}

        <NextTabButton
          label={ctaMessagerie.label}
          hidden={ctaMessagerie.hidden}
          onClick={() => goToTab?.(ctaMessagerie.target)}
        />
      </div>

      {/* Aperçu du mail reçu (notification si retenu, message de rejet sinon) */}
      {isPreviewOpen && (
        <div className="opms__marche-modal-overlay" onClick={() => setIsPreviewOpen(false)}>
          <div className="opms__marche-modal" onClick={(e) => e.stopPropagation()}>
            <div className="opms__marche-modal-bar">
              <span className="opms__marche-modal-title">
                {dateNotification
                  ? `Reçu le ${dateNotification}`
                  : position === 'retenu'
                    ? 'Aperçu du message de notification'
                    : 'Aperçu du message de rejet'}
              </span>
              <div className="opms__marche-modal-actions">
                <Button value="Fermer" variant="primary" onClick={() => setIsPreviewOpen(false)} />
              </div>
            </div>
            <MailPreview selectedCommandeForPreview={retenu} generateMailPreview={generateMail} />
          </div>
        </div>
      )}

      <ValidationPopUp
        isOpen={errorModal !== null}
        title="Erreur"
        text={errorModal ?? ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        cancelLabel=""
        confirmLabel="OK"
      />
    </div>
  );
};

export default CommandeMsMarche;
